import { filterOnlyHavingParameterSet } from '../filters/byParameterName.js';
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import buildFiltersForGenericChart from '../filters/genericChartsFilters.js';
import { PitchData, ResultData, PitchReprocessed, HitData, SwingDecision, ScoringData } from '../enums/index.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import { ChartSeries, Series, BaseballResultsProcessor } from '../entities/index.js';
import ResultPair from '../valueObjects/ResultPair.js';
import classifiesAsContactOld from '../classificators/contactClassifier.js';
import classifiesAsWhiffOld from '../classificators/whiffClassifier.js';
import classifiesAsContact from '../classificators/pitchCall/classifiesAsContact.js';
import classifiesAsWhiff from '../classificators/pitchCall/classifiesAsWhiff.js';

const buildFiltersArray = function({
    // tagging context
    batterIds, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}) {
    let battingChartFilters = buildFiltersForGenericChart({
        batterIds, pitchResults, pitchTypes, zones, pitcherIds, pitcherHandedness,
        outs, balls, strikes, runnersOn
    });

    let filterOnlyWithParameter = filterOnlyHavingParameterSet(['pitch'].concat(ResultData.getDataPath(PitchData.StrikeZoneFront.key)));

    return battingChartFilters.concat(
        filterOnlyWithParameter,
    );
};

const getSeriesForContactChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    mightHaveNewScoring, customContactClassifier, customWhiffClassifier
) {
    const chartSeries = [];
    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

    if (typeof(mightHaveNewScoring) !== 'boolean') {
        throw new TypeError('mightHaveNewScoring must be boolean type');
    }

    if (typeof(customWhiffClassifier) !== 'undefined' &&  typeof(customWhiffClassifier) !== 'function') {
        throw new TypeError('customWhiffClassifier must be an function, leave undefined to use default implementation');
    }

    if (typeof(customContactClassifier) !== 'undefined' && typeof(customContactClassifier) !== 'function') {
        throw new TypeError('customContactClassifier must be an function, leave undefined to use default implementation');
    }

    const contactClassifier = (
        customContactClassifier
        || mightHaveNewScoring ? classifiesAsContact: classifiesAsContactOld
    );

    const whiffClassifier = (
        customWhiffClassifier
        || mightHaveNewScoring ? classifiesAsWhiff: classifiesAsWhiffOld
    );

    const chartFilters = buildFiltersArray({
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    const matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);
    const pairsWithLocation =  resultsProcessor.filterData(matchedResults, chartFilters);
    const pitchProperty = ResultPair.getPitchProperty();
    const matchedPitches = pairsWithLocation
        .filter(pair => pair && pair[pitchProperty])
        .map(pair => pair[pitchProperty]);

    // prepare series
    const contacts = new Series(SwingDecision.Contact.key, 2);
    const whiffs = new Series(SwingDecision.Whiff.key, 2);
    const unclassified = new Series(SwingDecision.Unclassified.key, 2);

    // iterate over results
    for (let i = 0; i < matchedPitches.length; i++) {
        const pitch = matchedPitches[i];
        const pitchId = pitch[ResultData.Id.key];
        const pitchLocation = resultsProcessor.getObjectContent(
            pitch, ResultData.getDataPath(PitchData.StrikeZoneFront.key)
        );

        if (!pitchLocation) {
            // not able to show it on the strike zone chart
            continue;
        }

        const formatted = StringifiedParameterFormatter(
            pitchLocation, ';', PitchData.StrikeZoneFront.stringifiedAs, parseFloat
        );

        if (!formatted) {
            // not able to show it on the strike zone chart
            continue;
        }

        const pitchCallOrResult = (
            mightHaveNewScoring
            // we need to look at ptich call
            ? resultsProcessor.getObjectContent(
                pitch, ResultData.getDataPath(ScoringData.PitchCall.key)
            )
            // old BBH, pitch result
            : resultsProcessor.getObjectContent(
                pitch, ResultData.getDataPath(PitchData.Result.key)
            )
        );

        if (contactClassifier(pitchCallOrResult)) {
            // TODO - if we need to mirror it -  x: formatted.X * -1,
            contacts.add(pitchId, formatted.X, formatted.Z);
        }
        // TODO - double check if these can overlap
        else if (whiffClassifier(pitchCallOrResult)) {
            whiffs.add(pitchId, formatted.X, formatted.Z);
        }
        else {
            // TODO - we actually can skip it as batter didn't made a swing
            // unclassified.add(pitchId, formatted.X, formatted.Z);
        }
    }

    const categories = [];
    if (contacts.length) {
        chartSeries.push(contacts);
        categories.push(contacts.name);
    }
    if (whiffs.length) {
        chartSeries.push(whiffs);
        categories.push(whiffs.name);
    }
    if (unclassified.length) {
        chartSeries.push(unclassified);
        categories.push(unclassified.name);
    }

    return new ChartSeries(chartSeries, categories);
};

const listUniqueParametersForStrikeZoneChart = function(
    results, customResultsProcessor,
    {
        // tagging context
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult,
    mightHaveNewScoring
) {
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        mightHaveNewScoring ? ResultData.getDataPath(ScoringData.PitchCall.key): ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    const resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    if (typeof(mightHaveNewScoring) !== 'boolean') {
        throw new TypeError('mightHaveNewScoring must be boolean type');
    }

    if (typeof(customWhiffClassifier) !== 'undefined' &&  typeof(customWhiffClassifier) !== 'function') {
        throw new TypeError('customWhiffClassifier must be an function, leave undefined to use default implementation');
    }

    if (typeof(customContactClassifier) !== 'undefined' && typeof(customContactClassifier) !== 'function') {
        throw new TypeError('customContactClassifier must be an function, leave undefined to use default implementation');
    }

    let chartFilters = buildFiltersArray({
        batterIds, pitchResults, pitchTypes, pitcherIds, zones, pitcherHandedness,
        outs, balls, strikes, runnersOn,
    });

    // match pitch and hit...
    let matchedResults = BaseballResultsProcessor.matchPitchesAndHits(results);

    let pairsWithLocation = resultsProcessor.filterData(matchedResults, chartFilters);

    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => ['pitch'].concat(x)),
        hitParameters.map(x => ['hit'].concat(x)),
        pitchToHitParamsToMerge,
        pairsWithLocation, // it is ok, hit and pitch filtering is included on parameters level
        pairsWithLocation,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getSeriesForContactChart;

export {
    getSeriesForContactChart, listUniqueParametersForStrikeZoneChart
};